package com.havn.app.core.billing

import com.havn.app.features.agency.domain.HavnSubscriptionTier

enum class HavnBillingPeriod {
    Monthly,
    Yearly
}

data class HavnStorePlanOption(
    val id: String,
    val tier: HavnSubscriptionTier,
    val period: HavnBillingPeriod,
    val price: String,
    val monthlyEquivalentPrice: String? = null,
    val productId: String
)

data class HavnStorePlans(
    val options: List<HavnStorePlanOption> = emptyList()
) {
    fun option(tier: HavnSubscriptionTier, period: HavnBillingPeriod): HavnStorePlanOption? =
        options.firstOrNull { it.tier == tier && it.period == period }
}

data class HavnBillingCustomerState(
    val activeEntitlementIds: Set<String> = emptySet(),
    val tier: HavnSubscriptionTier = HavnSubscriptionTier.Free
) {
    val hasActiveHavnSubscription: Boolean
        get() = tier != HavnSubscriptionTier.Free

    companion object {
        fun fromActiveEntitlements(activeEntitlementIds: Set<String>): HavnBillingCustomerState {
            val normalized = activeEntitlementIds.map { it.lowercase() }.toSet()
            val tier = when {
                normalized.any { it == "pro_agent" || it.contains("pro") } -> HavnSubscriptionTier.ProAgent
                normalized.any { it == "solo_agent" || it.contains("solo") } -> HavnSubscriptionTier.SoloAgent
                else -> HavnSubscriptionTier.Free
            }
            return HavnBillingCustomerState(
                activeEntitlementIds = activeEntitlementIds,
                tier = tier
            )
        }
    }
}

interface HavnBillingService {
    suspend fun loadPlans(appUserId: String): Result<HavnStorePlans>
    suspend fun purchase(option: HavnStorePlanOption, appUserId: String): Result<HavnBillingCustomerState>
    suspend fun restorePurchases(appUserId: String): Result<HavnBillingCustomerState>
}

expect fun createHavnBillingService(): HavnBillingService
